import type { MouseEvent } from "react";
import { Checkbox, H5, H6, Divider, Box, Flex } from "orcs-design-system";
import { useHistory } from "react-router-dom";
import { getSearchPath } from "src/consts/urlPaths";
import useRestricted from "src/app/Restricted/useRestricted";

import { useGlobalSearchContext } from "src/components/Search/GlobalSearchContext";
import {
  useWorkspace,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import {
  filterTypes,
  positionOccupancyStatus,
} from "../../../pages/SearchPage/SearchPage.config";
import { trackSearchFilter, createToggleFilterHandler } from "./filterHandlers";
import {
  useSyncFiltersToUrl,
  useParseFiltersFromUrl,
} from "./useFilterUrlSync";
import FilterTypeRadioGroup from "./FilterTypeRadioGroup";
import PositionStatusFilter from "./PositionStatusFilter";
import TeamTypeFilterSection from "./TeamTypeFilterSection";
import FilterSearchSection from "./FilterSearchSection";
import FilterSidebarContent from "../FilterSidebarContent";

type FilterSearchResultsMatch = {
  params: {
    id?: string;
    type?: string;
    searchTerm?: string;
  };
};

type FilterSearchResultsProps = {
  match: FilterSearchResultsMatch;
};

const FilterSearchResults = ({ match }: FilterSearchResultsProps) => {
  const [restrictedAsDirectory, isPowerUser, isAdminUser] = useRestricted();
  const isDirectoryUser = !!restrictedAsDirectory;
  // getFilters/getTagFilters are ADMIN/POWER only, so the refine block has no
  // data for any other role.
  const canRefineResults = !!(isPowerUser || isAdminUser);
  const canShowHiddenTeams = canRefineResults;

  const {
    searchTerm,
    peopleFilters,
    setPeopleFilters,
    defaultTeamTypes,
    setDefaultTeamTypes,
    addPeopleFilter,
    removePeopleFilter,
    addDefaultTeamType,
    removeDefaultTeamType,
    positionFilters,
    setPositionFilters,
    addPositionFilter,
    removePositionFilter,
    positionStatus,
    setPositionStatus,
    includeHiddenTeams,
    setIncludeHiddenTeams,
    debouncedSearchTerm,
  } = useGlobalSearchContext();
  const { enablePositionBackedVacancies } = useWorkspaceFeatureFlags();

  const { params } = match;
  const { type, searchTerm: urlSearchTerm } = params;
  const history = useHistory();
  // Use URL when in sync or on initial load (searchTerm undefined); debouncedSearchTerm when user has typed something different.
  const effectiveSearchTerm =
    searchTerm === urlSearchTerm || searchTerm == null
      ? urlSearchTerm
      : debouncedSearchTerm;

  const onHandleNavigate =
    (searchType: string, query: string | null | undefined) =>
    (e: MouseEvent) => {
      setPeopleFilters([]);
      setDefaultTeamTypes([]);
      setPositionFilters([]);
      setPositionStatus(positionOccupancyStatus.ALL);
      setIncludeHiddenTeams(false);
      history.push(getSearchPath(searchType, query));
      e.stopPropagation();
      e.preventDefault();
      trackSearchFilter({
        legacyProps: { setting: "filter", value: searchType },
        journeyProps: {
          filter_setting: "filter",
          filter_value: searchType,
          filter_checked: true,
        },
      });
    };

  const onHandlePositionStatus = (status: string) => (e: MouseEvent) => {
    setPositionStatus(status);
    trackSearchFilter({
      legacyProps: { setting: "position_status", value: status },
      journeyProps: {
        filter_setting: "position_status",
        filter_value: status,
        filter_checked: true,
      },
    });
    e.stopPropagation();
    e.preventDefault();
  };

  const onHandleSubfilter = createToggleFilterHandler(
    "sub_filter",
    addPeopleFilter,
    removePeopleFilter
  );
  const onHandlePositionFieldFilter = createToggleFilterHandler(
    "position_field_filter",
    addPositionFilter,
    removePositionFilter
  );
  const onHandleTeamType = createToggleFilterHandler(
    "team_type",
    addDefaultTeamType,
    removeDefaultTeamType,
    () => true
  );

  useSyncFiltersToUrl({
    type,
    effectiveSearchTerm,
    peopleFilters,
    defaultTeamTypes,
    positionFilters,
    positionStatus,
    includeHiddenTeams,
    isDirectoryUser,
    history,
  });

  useParseFiltersFromUrl({
    type,
    history,
    isDirectoryUser,
    canShowHiddenTeams,
    setPeopleFilters,
    setDefaultTeamTypes,
    setPositionFilters,
    setPositionStatus,
    setIncludeHiddenTeams,
  });

  const workspace = useWorkspace();

  return (
    <div id="keep-open-on-click-outside">
      <H5 fontWeight="bold">Search filters</H5>
      <Box my="r">
        <Divider light />
      </Box>
      <Flex flexDirection="column" gap="s">
        <FilterTypeRadioGroup
          type={type}
          enablePositionBackedVacancies={enablePositionBackedVacancies}
          onNavigate={(searchType) =>
            onHandleNavigate(searchType, effectiveSearchTerm)
          }
        />

        {!isDirectoryUser && type === filterTypes.PEOPLE && (
          <>
            <FilterSearchSection
              items={[
                { label: "First name", name: "firstName" },
                { label: "Last name", name: "surname" },
                { label: "Job Title", name: "jobTitle" },
              ]}
              onChange={onHandleSubfilter}
              checkedItems={peopleFilters}
            />
            {canRefineResults && (
              <Box mt="r">
                <H6 fontWeight="bold">Refine results:</H6>
                <FilterSidebarContent hideHeading />
              </Box>
            )}
          </>
        )}
        {!isDirectoryUser &&
          type === filterTypes.POSITIONS &&
          !!enablePositionBackedVacancies && (
            <PositionStatusFilter
              positionStatus={positionStatus}
              onSelectStatus={onHandlePositionStatus}
              positionFilters={positionFilters}
              onPositionFieldChange={onHandlePositionFieldFilter}
            />
          )}
        {!isDirectoryUser && type === filterTypes.TEAMS && (
          <>
            {canShowHiddenTeams && (
              <Checkbox
                label="Include hidden teams"
                name="include-hidden-teams"
                checked={includeHiddenTeams}
                onChange={(event) => {
                  setIncludeHiddenTeams(event.target.checked);
                }}
              />
            )}
            <TeamTypeFilterSection
              groupTypes={workspace?.config?.groupTypes}
              defaultTeamTypes={defaultTeamTypes}
              onToggleTeamType={onHandleTeamType}
            />
          </>
        )}
      </Flex>
    </div>
  );
};

export default FilterSearchResults;
